import type { useI18n } from 'vue-i18n'
import { PhraseEntry } from '@/api/models/translation/GetPhrasesResponse'

type LangStringObject = {
    englishName?: string | null
    germanName?: string | null
}

export const chooseLangString = <T extends LangStringObject>(
    locale: ReturnType<typeof useI18n>['locale'],
    object?: T,
    defaultValue: string = '-',
) => {
    if (!object) {
        return defaultValue
    }

    if (locale.value === 'en-GB') {
        return object.englishName ?? defaultValue
    }

    if (locale.value === 'de-DE') {
        return object.germanName ?? defaultValue
    }

    return defaultValue
}

export const composeLocaleObject = (localeEntries: PhraseEntry[]) => {
    const localeObject: Record<string, unknown> = {}

    const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

    for (const entry of localeEntries) {
        const keyPaths = entry.key.split('.')
        const lastKeyPath = keyPaths.pop()

        if (!lastKeyPath) {
            continue
        }

        let destinationObject: Record<string, unknown> = localeObject
        let isConflictingEntry = false

        for (const keyPath of keyPaths) {
            const nextValue = destinationObject[keyPath]

            if (nextValue === undefined) {
                destinationObject[keyPath] = {}
            }

            const currentValue = destinationObject[keyPath]

            if (!isRecord(currentValue)) {
                isConflictingEntry = true
                break
            }

            destinationObject = currentValue
        }

        if (isConflictingEntry) {
            continue
        }

        const existingValue = destinationObject[lastKeyPath]

        if (existingValue !== undefined && isRecord(existingValue)) {
            continue
        }

        destinationObject[lastKeyPath] = entry.value
    }

    return localeObject
}
